import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  PieChart,
  Calendar,
  DollarSign,
  Target,
  ArrowUpRight,
  ArrowDownLeft,
  Coffee,
  Car,
  ShoppingBag,
  Home,
  Plane,
  Smartphone,
  Menu
} from 'lucide-react'

const spendingData = [
  { category: 'Food & Dining', amount: 1200, percentage: 35, icon: Coffee, color: 'bg-orange-50 text-orange-600', trend: 12 },
  { category: 'Transportation', amount: 800, percentage: 23, icon: Car, color: 'bg-blue-50 text-blue-600', trend: -5 },
  { category: 'Shopping', amount: 650, percentage: 19, icon: ShoppingBag, color: 'bg-purple-50 text-purple-600', trend: 8 },
  { category: 'Housing', amount: 400, percentage: 12, icon: Home, color: 'bg-emerald-50 text-emerald-600', trend: 0 },
  { category: 'Travel', amount: 250, percentage: 7, icon: Plane, color: 'bg-pink-50 text-pink-600', trend: 25 },
  { category: 'Utilities', amount: 150, percentage: 4, icon: Smartphone, color: 'bg-slate-100 text-slate-600', trend: -2 }
]

const monthlyData = [
  { month: 'Jan', income: 5500, expenses: 3200, savings: 2300 },
  { month: 'Feb', income: 5500, expenses: 3800, savings: 1700 },
  { month: 'Mar', income: 6200, expenses: 3500, savings: 2700 },
  { month: 'Apr', income: 5500, expenses: 4100, savings: 1400 },
  { month: 'May', income: 5800, expenses: 3600, savings: 2200 },
  { month: 'Jun', income: 5500, expenses: 3900, savings: 1600 }
]

const goals = [
  { name: 'Emergency Fund', target: 10000, current: 7500, category: 'savings' },
  { name: 'Vacation Fund', target: 5000, current: 2800, category: 'travel' },
  { name: 'New Car', target: 25000, current: 12000, category: 'transport' },
  { name: 'Home Down Payment', target: 50000, current: 18000, category: 'housing' }
]

export default function AnalyticsPage({ onMenuClick }) {
  const [selectedPeriod, setSelectedPeriod] = useState('monthly')
  const [activeChart, setActiveChart] = useState('spending')

  const totalSpending = spendingData.reduce((sum, item) => sum + item.amount, 0)
  const totalIncome = monthlyData.reduce((sum, item) => sum + item.income, 0)
  const totalSavings = monthlyData.reduce((sum, item) => sum + item.savings, 0)
  const savingsRate = (totalSavings / totalIncome) * 100

  return (
    <div className="min-h-screen bg-white">
      {/* Top Bar */}
      <div className="border-b border-slate-200 bg-white sticky top-0 z-10">
        <div className="px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between gap-3">
            {/* Mobile Menu Button + Title */}
            <div className="flex items-center gap-3 flex-1">
              <button
                onClick={onMenuClick}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
              >
                <Menu className="w-6 h-6 text-slate-700" />
              </button>

              <div className="flex-1">
                <h1 className="text-xl md:text-2xl font-semibold text-slate-900">Analytics Dashboard</h1>
                <p className="text-xs md:text-sm text-slate-500 mt-0.5 hidden sm:block">Detailed insights into your financial patterns</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3 w-full sm:w-auto">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="flex-1 sm:flex-none px-3 md:px-4 py-2 bg-white border border-slate-200 rounded-lg text-xs md:text-sm text-slate-900 focus:outline-none focus:border-slate-900 transition-colors"
              >
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
              </select>

              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveChart('spending')}
                  className={`p-2 rounded-lg transition-colors ${
                    activeChart === 'spending' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  <PieChart className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setActiveChart('trends')}
                  className={`p-2 rounded-lg transition-colors ${
                    activeChart === 'trends' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500'
                  }`}
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-[1600px] mx-auto">
        {/* Key Metrics */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Total Income</p>
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">${totalIncome.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">Last 6 months</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Total Expenses</p>
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">${totalSpending.toLocaleString()}</p>
            <p className="text-xs text-slate-500 mt-1">This month</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Savings Rate</p>
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
                <Target className="w-4 h-4 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">{savingsRate.toFixed(1)}%</p>
            <p className="text-xs text-slate-500 mt-1">Average rate</p>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-slate-500 font-medium">Net Worth</p>
              <div className="w-8 h-8 bg-orange-50 rounded-lg flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-orange-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-slate-900">$127.5K</p>
            <p className="text-xs text-emerald-600 mt-1">+12% this year</p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Chart Area */}
          <div className="lg:col-span-2 space-y-6">
            {/* Spending Breakdown */}
            {activeChart === 'spending' && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-6">Spending by Category</h3>

                <div className="space-y-4">
                  {spendingData.map((item, index) => {
                    const IconComponent = item.icon
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color}`}>
                            <IconComponent className="w-5 h-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-semibold text-slate-900">{item.category}</h4>
                            <p className="text-xs text-slate-500">{item.percentage}% of total</p>
                          </div>
                        </div>

                        <div className="text-right">
                          <p className="text-sm font-bold text-slate-900">${item.amount.toLocaleString()}</p>
                          <div className={`flex items-center text-xs ${
                            item.trend > 0 ? 'text-red-600' : item.trend < 0 ? 'text-emerald-600' : 'text-slate-500'
                          }`}>
                            {item.trend > 0 ? <ArrowUpRight className="w-3 h-3" /> :
                             item.trend < 0 ? <ArrowDownLeft className="w-3 h-3" /> : null}
                            <span>{item.trend > 0 ? '+' : ''}{item.trend}%</span>
                          </div>
                        </div>
                      </motion.div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Monthly Trends */}
            {activeChart === 'trends' && (
              <div className="bg-white border border-slate-200 rounded-xl p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-6">Monthly Trends</h3>

                <div className="space-y-4">
                  {monthlyData.map((month, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-4 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-slate-900">{month.month} 2024</h4>
                        <div className="text-right text-xs">
                          <p className="text-emerald-600 font-semibold">+${month.income.toLocaleString()}</p>
                          <p className="text-red-600">-${month.expenses.toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4 text-center mb-3">
                        <div>
                          <p className="text-xs text-slate-500">Income</p>
                          <p className="text-sm font-semibold text-emerald-600">${month.income.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Expenses</p>
                          <p className="text-sm font-semibold text-red-600">${month.expenses.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Savings</p>
                          <p className="text-sm font-semibold text-blue-600">${month.savings.toLocaleString()}</p>
                        </div>
                      </div>

                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{ width: `${(month.savings / month.income) * 100}%` }}
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Financial Goals */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-5">Financial Goals</h3>

              <div className="space-y-4">
                {goals.map((goal, index) => {
                  const progress = (goal.current / goal.target) * 100
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="text-xs font-semibold text-slate-900">{goal.name}</h4>
                        <span className="text-xs text-slate-600">{progress.toFixed(0)}%</span>
                      </div>

                      <div className="flex justify-between text-xs text-slate-500 mb-2">
                        <span>${goal.current.toLocaleString()}</span>
                        <span>${goal.target.toLocaleString()}</span>
                      </div>

                      <div className="w-full bg-slate-100 rounded-full h-1.5">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${progress}%` }}
                          transition={{ duration: 1, delay: index * 0.2 }}
                          className="h-1.5 bg-blue-600 rounded-full"
                        />
                      </div>
                    </motion.div>
                  )
                })}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-5">Quick Stats</h3>

              <div className="space-y-3">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Avg Monthly Income</span>
                  <span className="font-semibold text-emerald-600">$5,667</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Avg Monthly Expenses</span>
                  <span className="font-semibold text-red-600">$3,667</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Highest Expense Category</span>
                  <span className="font-semibold text-slate-900">Food & Dining</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Best Savings Month</span>
                  <span className="font-semibold text-blue-600">March 2024</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Total Transactions</span>
                  <span className="font-semibold text-slate-900">247</span>
                </div>
              </div>
            </div>

            {/* Financial Health Score */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-5">Financial Health</h3>

              <div className="text-center mb-5">
                <div className="relative w-24 h-24 mx-auto mb-3">
                  <svg className="w-24 h-24 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e2e8f0"
                      strokeWidth="2"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      strokeDasharray="85, 100"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-slate-900">85</span>
                  </div>
                </div>
                <p className="text-sm font-semibold text-blue-600">Excellent</p>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Savings Rate</span>
                  <span className="text-emerald-600">✓ Good</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Debt-to-Income</span>
                  <span className="text-emerald-600">✓ Low</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Emergency Fund</span>
                  <span className="text-yellow-600">⚡ Building</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-slate-600">Investment Diversity</span>
                  <span className="text-emerald-600">✓ Good</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
